import React from 'react';
import { lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ProtectedRoute } from '../shared/components/ProtectedRoute';
import NotFound from '../shared/components/NotFound';

// Non-lazy (small + used on first load)
import RegisterClient from '../features/auth/pages/RegisterClient';
import RegisterTalent from '../features/auth/pages/RegisterTalent';
import RegisterContentCreator from '../features/auth/pages/RegisterContentCreator';
import EditUser from '../features/admin/pages/EditUser';
import ContentsManagement from '../features/admin/pages/ContentsManagement';
import CreateNewContents from '../features/admin/pages/ContentsNew';
import ContentShowcase from '../shared/components/ContentShowcase';
import CastingCTA from '../shared/components/CastingCTA';
import SpecialtiesManagement from '../features/admin/pages/SpecialtiesManagement';

// Public
const Home = lazy(() => import('../features/public/pages/Home'));
const About = lazy(() => import('../features/public/pages/About'));
const Services = lazy(() => import('../features/public/pages/Services'));
const Work = lazy(() => import('../features/public/pages/Work'));
const Content = lazy(() => import('../features/public/pages/Content'));
const Contact = lazy(() => import('../features/public/pages/Contact'));
const ContentForm = lazy(() => import('../features/public/pages/ContentBriefForm'));

// Auth
const Login = lazy(() => import('../features/auth/pages/Login'));
const Register = lazy(() => import('../features/auth/pages/RegisterRoleSelect'));

// Talent
const Talents = lazy(() => import('../features/talent/pages/Talents'));
const TalentProfile = lazy(() => import('../features/talent/pages/TalentProfile'));
const TalentDashboard = lazy(() => import('../features/talent/pages/TalentDashboard'));
const ContentCreatorProfile = lazy(() => import('../features/contentCreator/pages/ContentCreatorProfile'));

// Client
const CastingBriefForm = lazy(() => import('../features/client/pages/CastingBriefForm'));
const ClientProfile = lazy(() => import('../features/client/pages/ClientProfile'));
const ClientDashboard = lazy(() => import('../features/client/pages/ClientDashboard'));
const SubmitBlog = lazy(() => import('../features/client/pages/SubmitBlog'));

// Profile
const ProfilePage = lazy(() => import('../features/profile/ProfilePage'));

// Spotlight
const Spotlight = lazy(() => import('../features/spotlight/pages/Spotlight'));
const BlogPost = lazy(() => import('../features/spotlight/pages/BlogPost'));

// Admin
const AdminDashboard = lazy(() => import('../features/admin/pages/Dashboard'));
const UsersManagement = lazy(() => import('../features/admin/pages/UsersManagement'));
const TalentsManagement = lazy(() => import('../features/admin/pages/TalentsManagement'));
const ClientsManagement = lazy(() => import('../features/admin/pages/ClientsManagement'));
const BlogsManagement = lazy(() => import('../features/admin/pages/BlogsManagement'));
const BlogEditor = lazy(() => import('../features/admin/pages/BlogEditor'));
const BriefsManagement = lazy(() => import('../features/admin/pages/BriefsManagement'));
const CategoriesManagement = lazy(() => import('../features/admin/pages/CategoriesManagement'));
const WorksManagement = lazy(() => import('../features/admin/pages/WorksManagement'));
const AlbumsManagement = lazy(() => import('../features/admin/pages/AlbumsManagement'));
const NotificationsPage = lazy(() => import('../features/notifications/NotificationsPage'));
const ProfileUpdateRequests = lazy(() => import('../features/admin/pages/ProfileUpdateRequests'));
const QuestionsManagement = lazy(() => import('../features/admin/pages/QuestionsManagement'));
const QuotationsManagement = lazy(() => import('../features/admin/pages/QuotationsManagement'));
const ContactMessagesManagement = lazy(() => import('../features/admin/pages/ContactMessagesManagement'));
const TrustedBrandsManagement = lazy(() => import('../features/admin/pages/TrustedBrandsManagement'));
const ProjectTypesManagement = lazy(() => import('../features/admin/pages/ProjectTypesManagement'));
const AboutSectionSettings = lazy(() => import('../features/admin/pages/AboutSectionSettings'));
const FooterSettings = lazy(() => import('../features/admin/pages/FooterSettings'));
const TestimonialsManagement = lazy(() => import('../features/admin/pages/TestimonialsManagement'));

// Route guards
const AdminRoute = ({ element }: { element: React.ReactElement }) => (
  <ProtectedRoute element={element} requiredRoles={['admin']} />
);
const ClientRoute = ({ element }: { element: React.ReactElement }) => (
  <ProtectedRoute element={element} requiredRoles={['client']} />
);
const TalentRoute = ({ element }: { element: React.ReactElement }) => (
  <ProtectedRoute element={element} requiredRoles={['talent']} />
);
const AuthRoute = ({ element }: { element: React.ReactElement }) => (
  <ProtectedRoute element={element} />
);

const AppRoutes = () => (
  <Routes>
    {/* Public */}
    <Route path="/" element={<Home />} />
    <Route path="/about" element={<About />} />
    <Route path="/services" element={<Services />} />
    <Route path="/talents" element={<Talents />} />
    {/* <Route path="/clients" element={<Clients />} /> */}
    <Route path="/work" element={<Work />} />
    <Route path="/spotlight" element={<Spotlight />} />
    <Route path="/spotlight/:id" element={<BlogPost />} />
    <Route path="/content" element={<Content />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    <Route path="/register/client" element={<RegisterClient />} />
    <Route path="/register/talent" element={<RegisterTalent />} />
    <Route path="/register/content-creator" element={<RegisterContentCreator />} />
    <Route path="/creator/:id" element={<ContentCreatorProfile />} />
    <Route path="/creator/me" element={<AuthRoute element={<ContentCreatorProfile />} />} />

    {/* Dev/test */}
    <Route path="/ContentShowcase" element={<ContentShowcase />} />
    <Route path="/CastingCTA" element={<CastingCTA />} />

    {/* Authenticated */}
    <Route path="/talent/:id" element={<TalentProfile />} />
    <Route path="/content-form" element={<AuthRoute element={<ContentForm />} />} />

    <Route path="/profile" element={<AuthRoute element={<ProfilePage />} />} />
    <Route path="/dashboard/talent" element={<TalentRoute element={<TalentDashboard />} />} />

    {/* Client */}

    <Route path="/casting-brief" element={<ProtectedRoute requiredRoles={['client', 'admin']} element={<CastingBriefForm />} />} />
    <Route path="/client/profile" element={<ClientRoute element={<ClientProfile />} />} />
    <Route path="/dashboard/client" element={<ClientRoute element={<ClientDashboard />} />} />
    <Route path="/spotlight/submit" element={<ClientRoute element={<SubmitBlog />} />} />

    {/* Admin */}
    <Route path="/dashboard/admin" element={<AdminRoute element={<AdminDashboard />} />} />
    <Route path="/admin/works" element={<AdminRoute element={<WorksManagement />} />} />
    <Route path="/admin/albums" element={<AdminRoute element={<AlbumsManagement />} />} />
    <Route path="/admin/users" element={<AdminRoute element={<UsersManagement />} />} />
    <Route path="/admin/users/edit/:id" element={<AdminRoute element={<EditUser />} />} />
    <Route path="/admin/categories" element={<AdminRoute element={<CategoriesManagement />} />} />
    <Route path="/admin/talents" element={<AdminRoute element={<TalentsManagement />} />} />
    <Route path="/admin/clients" element={<AdminRoute element={<ClientsManagement />} />} />
    <Route path="/admin/blogs" element={<AdminRoute element={<BlogsManagement />} />} />
    <Route path="/admin/blogs/new" element={<AdminRoute element={<BlogEditor />} />} />
    <Route path="/admin/blogs/edit" element={<AdminRoute element={<BlogEditor />} />} />
    <Route path="/admin/specialties" element={<AdminRoute element={<SpecialtiesManagement />} />} />
    <Route path="/admin/briefs" element={<AdminRoute element={<BriefsManagement />} />} />
    <Route path="/admin/contents" element={<AdminRoute element={<ContentsManagement />} />} />
    <Route path="/admin/contents/new" element={<AdminRoute element={<CreateNewContents />} />} />
    <Route path="/admin/profile-updates" element={<AdminRoute element={<ProfileUpdateRequests />} />} />
    <Route path="/admin/questions" element={<AdminRoute element={<QuestionsManagement />} />} />
    <Route path="/admin/quotations" element={<AdminRoute element={<QuotationsManagement />} />} />
    <Route path="/admin/contact-messages" element={<AdminRoute element={<ContactMessagesManagement />} />} />
    <Route path="/admin/trusted-brands" element={<AdminRoute element={<TrustedBrandsManagement />} />} />
    <Route path="/admin/project-types" element={<AdminRoute element={<ProjectTypesManagement />} />} />
    <Route path="/admin/about-section" element={<AdminRoute element={<AboutSectionSettings />} />} />
    <Route path="/admin/footer-settings" element={<AdminRoute element={<FooterSettings />} />} />
    <Route path="/admin/testimonials" element={<AdminRoute element={<TestimonialsManagement />} />} />

    {/* Notifications */}
    <Route path="/notifications" element={<AuthRoute element={<NotificationsPage />} />} />

    {/* 404 */}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

export default AppRoutes;
